(function (root, factory) {
  if (typeof define === 'function' && define.amd) {
    define(['underscore', 'torso/modules/NestedModel', 'services/appointmentExtService'], factory);
  } else if (typeof exports === 'object') {
    // CommonJS-style exports
    module.exports = factory(require('underscore'),
                             require('backbone-torso/modules/NestedModel'),
                             require('./../urls').appointmentExtService);
  } else {
    throw 'Only AMD and CommonJS imports are supported.';
  }
}(this, function (_, TorsoNestedModel, appointmentExtService) {
  'use strict';

  var INSURANCE_POLICIES_PROPERTY_NAME = 'insurancePolicies';
  var GREETED_STATUSES = {
    KIOSK: 'KIOSK',
    STAFF: 'STAFF',
    HIDE: 'HIDE'
  };

  /**
   * Model representing an appointment
   * @class AppointmentModel
   * @constructor
   * @extends Torso.NestedModel
   */
  var AppointmentModel = TorsoNestedModel.extend({
    idAttribute: 'externalId',
    GREETED_STATUSES: GREETED_STATUSES,

    /**
     * Performs the initial save for an appointment model, creating it as a placeholder appointment
     * Placeholder appointments are not present in external systems, and are used in cases such as unscheduled appointments
     * @method createAsPlaceholder
     * @returns promise for saving the model
     */
    createAsPlaceholder: function() {
      return this.save(null, { url: appointmentExtService + '/placeholder' });
    },

    /**
     * @method getAppointmentInsurance
     * @param insurancePolicyVecnaId {String} the vecna id of the insurance to check on this appointment.
     * @return {Object} the attributes of the appointmentInsurance information.
     */
    getAppointmentInsurance: function(insurancePolicyVecnaId) {
      var appointmentInsurances = this.get(INSURANCE_POLICIES_PROPERTY_NAME);
      var appointmentInsuranceExample = this._createNewAppointmentInsurance(insurancePolicyVecnaId);
      var appointmentInsuranceInCollection = _.findWhere(appointmentInsurances, appointmentInsuranceExample);
      return appointmentInsuranceInCollection;
    },

    /**
     * Insurance will be inserted at the index position and all other insurances will be moved down.
     * @method addAppointmentInsurance
     * @param insurancePolicyVecnaId {String} the vecna id of the insurance policy to reorder.
     * @param [zeroBasedIndex=0] {Number} the index to insert the insurance at.
     */
    addAppointmentInsurance: function(insurancePolicyVecnaId, zeroBasedIndex) {
      zeroBasedIndex = zeroBasedIndex || 0;
      var appointmentInsurances = this.get(INSURANCE_POLICIES_PROPERTY_NAME);
      appointmentInsurances = _.reject(appointmentInsurances, this._selectAppointmentInsuranceByIdIteratee(insurancePolicyVecnaId));
      var newAppointmentInsurance = this._createNewAppointmentInsurance(insurancePolicyVecnaId);
      appointmentInsurances.splice(zeroBasedIndex, 0, newAppointmentInsurance);
      this.set(INSURANCE_POLICIES_PROPERTY_NAME, appointmentInsurances);
    },

    /**
     * @method getAppointmentInsuranceIndex
     * @param insurancePolicyVecnaId of the insurance policy to get the index of.
     * @returns {Number} the index of the insurance policy with the given vecna id.
     */
    getAppointmentInsuranceIndex: function(insurancePolicyVecnaId) {
      var appointmentInsurances = this.get(INSURANCE_POLICIES_PROPERTY_NAME);
      var insuranceIndexInAppointment = _.findIndex(appointmentInsurances, this._selectAppointmentInsuranceByIdIteratee(insurancePolicyVecnaId));
      return insuranceIndexInAppointment;
    },

    /**
     * @method removeAppointmentInsurance
     * @param insurancePolicyVecnaId {String} of the insurance to remove from this appointments list of insurances.
     */
    removeAppointmentInsurance: function(insurancePolicyVecnaId) {
      var appointmentInsurances = this.get(INSURANCE_POLICIES_PROPERTY_NAME);
      appointmentInsurances = _.reject(appointmentInsurances, this._selectAppointmentInsuranceByIdIteratee(insurancePolicyVecnaId));
      this.set(INSURANCE_POLICIES_PROPERTY_NAME, appointmentInsurances);
    },

    /**
     * @method hasAppointmentInsurance
     * @param insurancePolicyVecnaId {String} the vecna id of the insurance policy to check on this appointment.
     * @return {Boolean} true if the insurance is on the appointment's list of insurances, false otherwise.
     */
    hasAppointmentInsurance: function(insurancePolicyVecnaId) {
      var appointmentInsurances = this.get(INSURANCE_POLICIES_PROPERTY_NAME);
      var isInsuranceInCollection = _.some(appointmentInsurances, this._selectAppointmentInsuranceByIdIteratee(insurancePolicyVecnaId));
      return isInsuranceInCollection;
    },

    /**
     * @method _selectAppointmentInsuranceByPolicyVecnaId
     * @param insurancePolicyVecnaId {String} the id of the insurance to check against.
     * @param appointmentInsurance {Object} appointment insurance to compare.
     *   @param appointmentInsurance.insurancePolicyVecnaId {String} insurance policy vecna id to compare.
     * @returns {Boolean} true if the appointmentInsurance matches the insuranceExternalId, false otherwise.
     * @private
     */
    _selectAppointmentInsuranceByPolicyVecnaId: function(insurancePolicyVecnaId, appointmentInsurance) {
      return appointmentInsurance.insurancePolicyVecnaId === insurancePolicyVecnaId;
    },

    /**
     * Creates a selector (iteratee) suitable for underscore functions where the iteratee returns true if this insurancePolicyVecnaId
     * matches the insurancePolicyVecnaId of the object being checked.
     * @method _selectAppointmentInsuranceByIdIteratee
     * @param insurancePolicyVecnaId the vecna id of the insurance to match against.
     * @private
     */
    _selectAppointmentInsuranceByIdIteratee: function(insurancePolicyVecnaId) {
      return _.bind(this._selectAppointmentInsuranceByPolicyVecnaId, this, insurancePolicyVecnaId);
    },

    /**
     * Creates a new appointment insurance element.
     * @method _createNewAppointmentInsurance
     * @param insurancePolicyVecnaId the vecna id of the insurance policy to create the join for.
     * @returns {Object} the element of the appointment insurance join array.
     * @private
     */
    _createNewAppointmentInsurance: function(insurancePolicyVecnaId) {
      return {
        insurancePolicyVecnaId: insurancePolicyVecnaId
      };
    }
  });

  AppointmentModel.GREETED_STATUSES = GREETED_STATUSES;

  return AppointmentModel;
}));
